/**
 * RevisionsMenu — toolbar control for switching between saved
 * revisions of an SP-backed SLD project.
 *
 * Only renders when the editor was opened with `?project=<qid>` in
 * the URL (i.e. there's an active project context in the store).
 * Standalone editor sessions don't see this control at all — there's
 * no revision system to navigate.
 *
 * Behaviour:
 *   - The button shows the current revision's label + a small badge
 *     (AUTO / MANUAL) so the user always knows what they're editing.
 *   - Clicking opens a dropdown listing every revision for this
 *     project, newest first.
 *   - Clicking a row loads that revision (replaces topology + clears
 *     nodes so autoLayout re-runs).
 *   - "Save current as new" creates a manual revision from the
 *     current canvas state.
 *   - "Update this revision" overwrites the loaded revision with
 *     the current canvas state.
 *   - "New from project data" regenerates a fresh auto revision
 *     from the SP backend (project-topology.php) — useful when the
 *     user has updated equipment in the SP dashboard and wants the
 *     SLD to reflect those changes.
 */
import { useEffect, useRef, useState } from 'react';
import { useEditorStore } from '../store/editor';
import {
  listRevisions,
  loadRevision,
  createRevision,
  type RevisionSummary,
} from '../api/revisions';
import { fetchProjectTopology, summariseProjectTopology } from '../api/loadProjectFromUrl';

export function RevisionsMenu() {
  const projectId      = useEditorStore((s) => s.projectId);
  const revisionId     = useEditorStore((s) => s.revisionId);
  const revisionLabel  = useEditorStore((s) => s.revisionLabel);
  const revisionSource = useEditorStore((s) => s.revisionSource);
  const saveStatus     = useEditorStore((s) => s.saveStatus);
  const lastSavedAt    = useEditorStore((s) => s.lastSavedAt);
  const topology       = useEditorStore((s) => s.topology);
  const loadTopology   = useEditorStore((s) => s.loadTopology);
  const setProjectContext = useEditorStore((s) => s.setProjectContext);
  const setProjectSummary = useEditorStore((s) => s.setProjectSummary);

  const [open, setOpen]       = useState(false);
  const [list, setList]       = useState<RevisionSummary[]>([]);
  const [busy, setBusy]       = useState(false);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Lazy-load the revision list on first open. Re-fetch on each
  // open so a user who saved a new revision in another tab sees it.
  // Hook is called unconditionally (Rules of Hooks) — the body
  // bails out when there's no project / the menu isn't open.
  useEffect(() => {
    if (!open || !projectId) return;
    setBusy(true);
    listRevisions(projectId)
      .then((rows) => {
        setList(rows);
        setLoadedFor(projectId);
      })
      .catch((err) => {
        console.warn('[Revisions] list failed:', err);
      })
      .finally(() => setBusy(false));
  }, [open, projectId]);

  // Click outside to close.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  // Standalone mode — no SP project context, no menu. Early-return
  // is AFTER all hook calls so React sees a consistent hook count
  // across renders even when projectId flips between null and set.
  if (!projectId) return null;

  const switchTo = async (rev: RevisionSummary) => {
    if (rev.id === revisionId) { setOpen(false); return; }
    setBusy(true);
    try {
      const full = await loadRevision(rev.id);
      loadTopology(full.topology);
      setProjectContext({
        projectId: projectId!,
        revisionId: full.id,
        revisionLabel: full.label,
        revisionSource: full.source,
      });
      setOpen(false);
    } catch (err) {
      console.warn('[Revisions] load failed:', err);
    } finally {
      setBusy(false);
    }
  };

  const saveCurrentAsNew = async () => {
    setBusy(true);
    try {
      const created = await createRevision({
        qid: projectId!,
        topology,
        source: 'manual',
        label: `Edit · ${formatNow()}`,
      });
      setProjectContext({
        projectId: projectId!,
        revisionId: created.id,
        revisionLabel: created.label,
        revisionSource: 'manual',
      });
      // Refresh the list so the new entry shows.
      const fresh = await listRevisions(projectId!);
      setList(fresh);
    } catch (err) {
      console.warn('[Revisions] save-new failed:', err);
    } finally {
      setBusy(false);
    }
  };

  // updateCurrent removed — auto-save (useAutoSaveRevision) handles
  // pushing edits back to the loaded revision automatically. The
  // status indicator next to the Rev button reflects the latest
  // save state.

  const generateFromProject = async () => {
    setBusy(true);
    try {
      const fresh = await fetchProjectTopology(projectId!);
      if (!fresh) {
        console.warn('[Revisions] project-topology fetch returned null');
        return;
      }
      const created = await createRevision({
        qid: projectId!,
        topology: fresh,
        source: 'auto',
        label: `Auto · ${formatNow()}`,
      });
      loadTopology(fresh);
      setProjectContext({
        projectId: projectId!,
        revisionId: created.id,
        revisionLabel: created.label,
        revisionSource: 'auto',
      });
      // Refresh the "Linked Project" card with the freshly-fetched
      // SP state. Without this the card still shows the OLD summary
      // from whenever the editor was first loaded.
      setProjectSummary(summariseProjectTopology(fresh));
      const list2 = await listRevisions(projectId!);
      setList(list2);
      setOpen(false);
    } catch (err) {
      console.warn('[Revisions] generateFromProject failed:', err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={wrapRef} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <SaveIndicator status={saveStatus} lastSavedAt={lastSavedAt} />
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setOpen((v) => !v)}
          title={`Project ${projectId} · ${revisionLabel || 'no revision'}`}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <span style={{ fontSize: 12, opacity: 0.85 }}>Rev</span>
          <span style={{ fontWeight: 600, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {revisionLabel || '—'}
          </span>
          {revisionSource && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: 0.5,
                padding: '2px 6px',
                borderRadius: 4,
                background: revisionSource === 'auto' ? 'var(--info-50)' : 'var(--surface-3)',
                color: revisionSource === 'auto' ? 'var(--info-700)' : 'var(--text-muted)',
                textTransform: 'uppercase',
              }}
            >
              {revisionSource}
            </span>
          )}
          <span style={{ fontSize: 10, opacity: 0.6 }}>▾</span>
        </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 6,
            minWidth: 360,
            maxHeight: 440,
            overflowY: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 1000,
            padding: '6px 0',
          }}
        >
          {/* Action row at the top — always visible regardless of
              whether revisions have loaded yet. Edits to the current
              revision auto-save; this row is for branching (snapshot
              as new) and refreshing from project data. */}
          <div style={{ padding: '4px 8px', display: 'flex', gap: 6, borderBottom: '1px solid var(--border)' }}>
            <button
              type="button"
              className="btn-tertiary"
              onClick={saveCurrentAsNew}
              disabled={busy}
              style={{ flex: 1, fontSize: 11 }}
              title="Snapshot the current canvas as a new revision (the original stays untouched)"
            >
              + New revision
            </button>
            <button
              type="button"
              className="btn-tertiary"
              onClick={generateFromProject}
              disabled={busy}
              style={{ flex: 1, fontSize: 11 }}
              title="Regenerate a fresh diagram from the current project data (saved as a new auto revision)"
            >
              ↻ From project
            </button>
          </div>

          {/* Revision list */}
          <div style={{ padding: '4px 0' }}>
            {busy && loadedFor !== projectId && (
              <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>
            )}
            {!busy && list.length === 0 && (
              <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-muted)' }}>
                No revisions yet — save one to get started.
              </div>
            )}
            {list.map((rev) => {
              const isCurrent = rev.id === revisionId;
              return (
                <button
                  key={rev.id}
                  type="button"
                  onClick={() => switchTo(rev)}
                  disabled={busy}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    width: '100%',
                    padding: '8px 14px',
                    background: isCurrent ? 'var(--accent-soft)' : 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    borderLeft: `3px solid ${isCurrent ? 'var(--accent)' : 'transparent'}`,
                  }}
                  onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.background = 'var(--surface-2)'; }}
                  onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {rev.label}
                    </div>
                    {rev.summary && (
                      <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2 }}>
                        {rev.summary}
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: 'var(--text-faint)', marginTop: 2 }}>
                      {formatTimestamp(rev.updated_at)}
                    </div>
                  </div>
                  <span
                    style={{
                      flexShrink: 0,
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: rev.source === 'auto' ? 'var(--info-50)' : 'var(--surface-3)',
                      color: rev.source === 'auto' ? 'var(--info-700)' : 'var(--text-muted)',
                      textTransform: 'uppercase',
                    }}
                  >
                    {rev.source}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

/**
 * Compact "Saving… / Saved · 12:34 / Save failed" indicator that
 * sits to the LEFT of the Rev button. Hidden in idle state on the
 * very first render (lastSavedAt === null) so the toolbar isn't
 * cluttered before the first save fires.
 */
function SaveIndicator({
  status,
  lastSavedAt,
}: {
  status: 'idle' | 'saving' | 'saved' | 'error';
  lastSavedAt: number | null;
}) {
  if (status === 'idle' && lastSavedAt === null) return null;

  let text = '';
  let color = 'var(--text-muted)';
  if (status === 'saving') {
    text = 'Saving…';
    color = 'var(--text-muted)';
  } else if (status === 'error') {
    text = 'Save failed';
    color = 'var(--danger-700)';
  } else if (lastSavedAt !== null) {
    text = `Saved · ${formatHHMM(lastSavedAt)}`;
    color = 'var(--text-muted)';
  }
  return (
    <span
      style={{
        fontSize: 11,
        color,
        whiteSpace: 'nowrap',
        opacity: 0.85,
      }}
      title={lastSavedAt !== null ? new Date(lastSavedAt).toLocaleString() : undefined}
    >
      {text}
    </span>
  );
}

function formatHHMM(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** "12 May 2026 14:30" — short enough for the dropdown row. */
function formatTimestamp(s: string): string {
  if (!s) return '';
  // Backend sends 'YYYY-MM-DD HH:MM:SS' (MySQL DATETIME).
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatNow(): string {
  return new Date().toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}
